import { ClientActionCacheService } from "@cervval.js/client-action/src/services/client.action.cache.service";
import { DefaultClientAuthenticationService } from "@cervval.js/client-auth/src/default.clientAuthentication.service";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { CLIENT_INFO } from "@cervval.js/client-html5-core/src/tools/clientInfo";
import { H_100, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { configureNavigation } from "@cervval.js/client-html5-spa";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { ClientRouteOptions, TypedClientAjaxProxy } from "@cervval.js/client-html5-spa/routes";
import { showErrorInDialog } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { ACTION_CACHE_SERVICE } from "@cervval.js/common-action/src/services/cache.service";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { ChangelogData } from "@cervval.js/common/src/models/changelog.model";
import { ensureLoaded, objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { PublicBookingRoutes } from "@ehealth_tools/common-booking/src/routes/publicBooking.routes";
import { SimpleLoginRoutes } from "@ehealth_tools/simple-login-common/src/simpleLogin.routes";
import { promptTermsAcceptationInDialogIfNeeded } from "@sportsante/client-shared/src/components/cgvComponents/acceptTerms/acceptTerms.tools";
import { FetchDataTypes, FetchDataUID } from "@sportsante/common-shared/src/data/global/fetchData.config";
import { ElementTypes, fetchableElementTypeNames } from "@sportsante/common-shared/src/data/global/global.model";
import { isCoachDTO } from "@sportsante/common-shared/src/data/users/user.tools";
import { ActivmedAuthRoutes } from "@sportsante/common-shared/src/routes/auth.routes";
import { getNavigationServiceConfiguration } from "../../../core/pages/navigation.config";
import { registerServices } from "../../../core/services/registerServices";
import { NavHeader } from "../navHeader/navHeader";

/**
 * ensureLoaded is needed because we don't reference these components anywhere else in the application and nav-header tag is not known by browser otherwise
 */
ensureLoaded(NavHeader);

@CustomElement({
    name: "app-component",
    style: require("./app.scss"),
    template: require("./app.html"),
    elementCssClasses: [W_100, H_100]
})
export class App extends WebComponent {

    @TypedHtmlElements()
    public readonly components!: {
        navHeader: NavHeader
    };

    @TypedClientAjaxProxy()
    private readonly _authRoutes!: ActivmedAuthRoutes<ClientRouteOptions>;

    @TypedClientAjaxProxy()
    private readonly _simpleLoginRoutes!: SimpleLoginRoutes<ClientRouteOptions>;

    @TypedClientAjaxProxy()
    private readonly _publicBookingRoutes!: PublicBookingRoutes<ElementTypes, ClientRouteOptions>;

    @Injected(ACTION_CACHE_SERVICE)
    private readonly _actionCacheService!: ClientActionCacheService<ElementTypes, FetchDataTypes>;

    constructor() {
        super();
        // Register services before loading DOM

        // Register App version (to detect server updates)
        const changelog = require("@sportsante/common-shared/src/sportsante.changelog.json") as ChangelogData;
        CLIENT_INFO.appVersion = changelog.versions[0].uid;

        // Register services
        registerServices();

        // Set App title
        document.title = "ActivMed - L'ordonnance d'une vie meilleure";
    }

    /**
     * Called when component is added to the DOM for the first time
     */
    public override async init(): Promise<void> {


        // Fetch data about user auth to update navHeader display and fetch data
        await this._simpleLoginRoutes.amIConnected(undefined).then(async res => {
            this.components.navHeader.components.disconnected.classList.remove("d-none");
            this.components.navHeader.components.connected.classList.add("d-none");
            if (res === true) {
                const loginData = await this._authRoutes.refreshLoginData();

                if (loginData) {
                    // TODO Restrain fetch tables : only the one mandatory for booking ?
                    await this._actionCacheService.fetchDataIfNeeded(FetchDataUID.GET_ALL, {
                        retrieveDeleted: true
                    }, fetchableElementTypeNames);
                    injector.register(new DefaultClientAuthenticationService(loginData));
                    this.components.navHeader.components.userLink.innerHTML = loginData.user.familyName + " " + loginData.user.givenName;
                    this.components.navHeader.components.disconnected.classList.add("d-none");
                    this.components.navHeader.components.connected.classList.remove("d-none");
                    const currentUser = this._actionCacheService.getDTOCacheRepository("user_profiles").getDTOById(loginData.user.id);

                    if (isCoachDTO(currentUser)) {
                        this.components.navHeader.components.navAppButton.classList.remove("d-none");
                    } else {
                        this.components.navHeader.components.navAppButton.classList.add("d-none");
                    }
                    // Get consent (CGU/CGV)
                    promptTermsAcceptationInDialogIfNeeded(currentUser.id);
                } else {
                    throw new Error("Unable to get the loginData while connected");
                }

            }
        }).catch(showErrorInDialog);

        const advertisingData = await this._publicBookingRoutes.getAdvertisingProfessionalData();
        for (const table of objectKeys(advertisingData)) {
            const data = advertisingData[table];
            if (data) {
                for (const dto of data) {
                    dto.__tableName = table;
                }
            }
            if (data) {
                this._actionCacheService.getDTOCacheRepository(table).pushAll(data);
            }
        }

        configureNavigation(getNavigationServiceConfiguration());

    }
}